import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mic, Square, Play, Pause, ChevronDown, Save, Trash2, Clock, Sparkles, CheckCircle } from 'lucide-react';
import { saveReadingRecording } from '../services/recordingService';
import { isMediaDevicesSupported, isSecureContext, getMediaErrorDetails } from '@/lib/media';

interface ReadingRecorderProps {
  storybookId: string;
  storybookTitle: string;
  currentPage: number;
  totalPages: number;
  onRecordingSaved?: (recordingUrl: string) => void;
  // Embedded mode - hide floating button, control via external trigger
  embedded?: boolean;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  // Callback when recording state changes (for external UI updates)
  onRecordingStateChange?: (isRecording: boolean) => void;
}

type RecorderState = 'idle' | 'prompt-start' | 'recording' | 'paused' | 'prompt-end' | 'saving' | 'saved';

export const ReadingRecorder: React.FC<ReadingRecorderProps> = ({
  storybookId,
  storybookTitle,
  currentPage,
  totalPages,
  onRecordingSaved,
  embedded = false,
  isOpen,
  onOpenChange,
  onRecordingStateChange
}) => {
  const [state, setState] = useState<RecorderState>('idle');
  
  // Notify parent when recording state changes
  useEffect(() => {
    const isRecording = state === 'recording' || state === 'paused';
    onRecordingStateChange?.(isRecording);
  }, [state, onRecordingStateChange]);
  const [internalExpanded, setInternalExpanded] = useState(false);
  
  // Use controlled or uncontrolled mode
  const isExpanded = embedded ? (isOpen ?? false) : internalExpanded;
  const setIsExpanded = (value: boolean) => {
    if (embedded && onOpenChange) {
      onOpenChange(value);
    } else {
      setInternalExpanded(value);
    }
  };
  const [duration, setDuration] = useState(0);
  const [showStartPrompt, setShowStartPrompt] = useState(false);
  const [showEndPrompt, setShowEndPrompt] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [hasShownStartPrompt, setHasShownStartPrompt] = useState(false);
  const [hasShownEndPrompt, setHasShownEndPrompt] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // Show start prompt after a short delay on first page
  useEffect(() => {
    if (currentPage === 1 && !hasShownStartPrompt && state === 'idle') {
      const timer = setTimeout(() => {
        setShowStartPrompt(true);
        setHasShownStartPrompt(true);
        // Auto-hide after 5 seconds
        setTimeout(() => setShowStartPrompt(false), 5000);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [currentPage, hasShownStartPrompt, state]);

  // Show end prompt on last page
  useEffect(() => {
    if (currentPage === totalPages && !hasShownEndPrompt && state === 'recording') {
      setShowEndPrompt(true);
      setHasShownEndPrompt(true);
    }
  }, [currentPage, totalPages, hasShownEndPrompt, state]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const startRecording = async () => {
    // Pre-flight check for MediaDevices API
    if (!isMediaDevicesSupported()) {
      const message = !isSecureContext()
        ? 'Microphone requires HTTPS. Please use a secure connection.'
        : 'Your browser does not support microphone access.';
      console.error('[ReadingRecorder]', message);
      return;
    }
    
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        } 
      });
      
      streamRef.current = stream;
      
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus') 
        ? 'audio/webm;codecs=opus' 
        : 'audio/mp4';
      
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: mimeType });
        const url = URL.createObjectURL(blob);
        setAudioBlob(blob);
        setAudioUrl(url);
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorder.start(100);
      setState('recording');
      setShowStartPrompt(false);
      setDuration(0);
      
      timerRef.current = setInterval(() => {
        setDuration(prev => prev + 1);
      }, 1000);
      
    } catch (err) {
      console.error('Failed to start recording:', err);
    }
  };

  const pauseRecording = () => {
    if (mediaRecorderRef.current && state === 'recording') {
      mediaRecorderRef.current.pause();
      setState('paused');
      if (timerRef.current) clearInterval(timerRef.current);
    }
  };

  const resumeRecording = () => {
    if (mediaRecorderRef.current && state === 'paused') {
      mediaRecorderRef.current.resume();
      setState('recording');
      timerRef.current = setInterval(() => {
        setDuration(prev => prev + 1);
      }, 1000);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && (state === 'recording' || state === 'paused')) {
      mediaRecorderRef.current.stop();
      if (timerRef.current) clearInterval(timerRef.current);
      setState('prompt-end');
      setShowEndPrompt(false);
    }
  };

  const discardRecording = () => {
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    setAudioUrl(null);
    setAudioBlob(null);
    setDuration(0);
    setState('idle');
    setIsExpanded(false);
    setHasShownEndPrompt(false);
    setSaveError(null);
  };

  const saveRecording = async () => {
    if (!audioBlob) return;
    
    setState('saving');
    setSaveError(null);
    
    try {
      const result = await saveReadingRecording({
        storybookId,
        audioBlob,
        durationSeconds: duration
      });

      if (result.success) {
        setState('saved');
        onRecordingSaved?.(result.recording?.audioUrl || audioUrl || '');
        
        // Auto-close after showing success
        setTimeout(() => {
          if (audioUrl) URL.revokeObjectURL(audioUrl);
          setAudioUrl(null);
          setAudioBlob(null);
          setDuration(0);
          setState('idle');
          setIsExpanded(false);
          setHasShownStartPrompt(false);
          setHasShownEndPrompt(false);
        }, 2000);
      } else {
        setSaveError(result.error || 'Failed to save');
        setState('prompt-end');
      }
    } catch (error) {
      console.error('Save error:', error);
      setSaveError('Failed to save recording');
      setState('prompt-end');
    }
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleIconClick = () => {
    if (state === 'idle') {
      setShowStartPrompt(false);
      setIsExpanded(true);
    } else {
      setIsExpanded(!isExpanded);
    }
  };

  // Expose trigger for external control
  const handleExternalTrigger = () => {
    setShowStartPrompt(false);
    setIsExpanded(true);
  };

  return (
    <>
      {/* Recorder Container - fixed when standalone, relative when embedded */}
      <div className={embedded ? "relative z-50" : "fixed bottom-6 right-6 z-50"}>
        <AnimatePresence>
          {/* Start Prompt Bubble */}
          {showStartPrompt && state === 'idle' && (
            <motion.div
              initial={{ opacity: 0, y: 10, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.9 }}
              className="absolute bottom-full right-0 mb-3 w-64"
            >
              <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-blue-100 rounded-xl flex-shrink-0">
                    <Sparkles className="w-4 h-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Capture this moment?</p>
                    <p className="text-xs text-gray-500 mt-1">Record your child reading this story</p>
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={() => {
                      setShowStartPrompt(false);
                      setIsExpanded(true);
                    }}
                    className="flex-1 px-3 py-1.5 bg-blue-600 text-[#1A1A1A] text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Yes, record
                  </button>
                  <button
                    onClick={() => setShowStartPrompt(false)}
                    className="px-3 py-1.5 text-gray-500 text-xs font-medium hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    Not now
                  </button>
                </div>
                {/* Arrow */}
                <div className="absolute -bottom-2 right-6 w-4 h-4 bg-white border-r border-b border-gray-100 transform rotate-45" />
              </div>
            </motion.div>
          )}

          {/* End Prompt Bubble */}
          {showEndPrompt && state === 'recording' && (
            <motion.div
              initial={{ opacity: 0, y: 10, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.9 }}
              className="absolute bottom-full right-0 mb-3 w-56"
            >
              <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-green-100 rounded-xl flex-shrink-0">
                    <Sparkles className="w-4 h-4 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Great reading!</p>
                    <p className="text-xs text-gray-500 mt-1">Finish when ready</p>
                  </div>
                </div>
                <button
                  onClick={stopRecording}
                  className="w-full mt-3 px-3 py-2 bg-green-600 text-[#1A1A1A] text-xs font-medium rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                >
                  <Square className="w-3 h-3" />
                  Stop & Save Recording
                </button>
                <div className="absolute -bottom-2 right-6 w-4 h-4 bg-white border-r border-b border-gray-100 transform rotate-45" />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Click outside overlay to minimize */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40"
              onClick={() => setIsExpanded(false)}
            />
          )}
        </AnimatePresence>

        {/* Expanded Controls */}
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 10 }}
              className="absolute bottom-full right-0 mb-3 z-50"
            >
              <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-4 min-w-[280px]">
                {/* Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Mic className="w-4 h-4 text-gray-400" />
                    <span className="text-sm font-medium text-gray-700">Reading Recorder</span>
                  </div>
                  <button
                    onClick={() => setIsExpanded(false)}
                    className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors flex items-center gap-1 text-gray-400 hover:text-gray-600"
                    title="Minimize"
                  >
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </div>

                {/* Idle State */}
                {state === 'idle' && !audioUrl && (
                  <div className="text-center py-4">
                    <div className="w-16 h-16 mx-auto mb-3 rounded-full bg-blue-100 flex items-center justify-center">
                      <Mic className="w-8 h-8 text-blue-600" />
                    </div>
                    <p className="text-sm text-gray-600 mb-4">Record your child reading<br />"{storybookTitle}"</p>
                    <button
                      onClick={startRecording}
                      className="w-full px-4 py-2.5 bg-blue-600 text-[#1A1A1A] rounded-xl font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
                    >
                      <Mic className="w-4 h-4" />
                      Start Recording
                    </button>
                  </div>
                )}

                {/* Recording State */}
                {(state === 'recording' || state === 'paused') && (
                  <div className="text-center py-2">
                    {/* Recording Indicator */}
                    <div className="flex items-center justify-center gap-2 mb-3">
                      {state === 'recording' && (
                        <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
                      )}
                      <span className="text-2xl font-mono font-bold text-gray-900">
                        {formatTime(duration)}
                      </span>
                    </div>
                    
                    {/* Waveform Animation */}
                    {state === 'recording' && (
                      <div className="flex items-center justify-center gap-1 h-8 mb-4">
                        {[...Array(12)].map((_, i) => (
                          <motion.div
                            key={i}
                            className="w-1 bg-blue-500 rounded-full"
                            animate={{
                              height: [8, 24, 8],
                            }}
                            transition={{
                              duration: 0.5,
                              repeat: Infinity,
                              delay: i * 0.05,
                            }}
                          />
                        ))}
                      </div>
                    )}

                    {state === 'paused' && (
                      <p className="text-sm text-amber-600 mb-4">Recording paused</p>
                    )}

                    {/* Controls */}
                    <div className="flex items-center justify-center gap-3">
                      {state === 'recording' ? (
                        <button
                          onClick={pauseRecording}
                          className="p-3 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors"
                          title="Pause"
                        >
                          <Pause className="w-5 h-5 text-gray-700" />
                        </button>
                      ) : (
                        <button
                          onClick={resumeRecording}
                          className="p-3 bg-blue-100 rounded-full hover:bg-blue-200 transition-colors"
                          title="Resume"
                        >
                          <Play className="w-5 h-5 text-blue-700" />
                        </button>
                      )}
                      <button
                        onClick={stopRecording}
                        className="p-3 bg-red-100 rounded-full hover:bg-red-200 transition-colors"
                        title="Stop"
                      >
                        <Square className="w-5 h-5 text-red-600" />
                      </button>
                    </div>

                    <p className="text-xs text-gray-400 mt-3">
                      Page {currentPage} of {totalPages}
                    </p>
                  </div>
                )}

                {/* Review/Save State */}
                {state === 'prompt-end' && audioUrl && (
                  <div className="text-center py-2">
                    <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-green-100 flex items-center justify-center">
                      <Sparkles className="w-6 h-6 text-green-600" />
                    </div>
                    <p className="text-sm font-medium text-gray-900 mb-1">Great reading session!</p>
                    <p className="text-xs text-gray-500 mb-3">
                      {formatTime(duration)} recorded
                    </p>
                    
                    {/* Error Message */}
                    {saveError && (
                      <div className="mb-3 p-2 bg-red-50 text-red-600 text-xs rounded-lg">
                        {saveError}
                      </div>
                    )}
                    
                    {/* Audio Preview */}
                    <audio src={audioUrl} controls className="w-full mb-4 h-10" />
                    
                    <div className="flex gap-2">
                      <button
                        onClick={discardRecording}
                        className="flex-1 px-3 py-2 border border-gray-200 text-gray-600 rounded-lg font-medium hover:bg-gray-50 transition-colors flex items-center justify-center gap-1"
                      >
                        <Trash2 className="w-4 h-4" />
                        Discard
                      </button>
                      <button
                        onClick={saveRecording}
                        className="flex-1 px-3 py-2 bg-green-600 text-[#1A1A1A] rounded-lg font-medium hover:bg-green-700 transition-colors flex items-center justify-center gap-1"
                      >
                        <Save className="w-4 h-4" />
                        Save
                      </button>
                    </div>
                  </div>
                )}

                {/* Saving State */}
                {state === 'saving' && (
                  <div className="text-center py-6">
                    <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-blue-100 flex items-center justify-center animate-pulse">
                      <Save className="w-6 h-6 text-blue-600" />
                    </div>
                    <p className="text-sm text-gray-600">Saving recording...</p>
                  </div>
                )}

                {/* Saved Success State */}
                {state === 'saved' && (
                  <div className="text-center py-6">
                    <motion.div 
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="w-12 h-12 mx-auto mb-3 rounded-full bg-green-100 flex items-center justify-center"
                    >
                      <CheckCircle className="w-6 h-6 text-green-600" />
                    </motion.div>
                    <p className="text-sm font-medium text-green-600">Recording saved!</p>
                    <p className="text-xs text-gray-500 mt-1">Check your recordings library</p>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main Floating Button - hidden when embedded (controlled by parent) */}
        {!embedded && (
          <motion.button
            onClick={handleIconClick}
            data-recorder-trigger
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className={`relative w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-colors ${
              state === 'recording' 
                ? 'bg-red-500 hover:bg-red-600' 
                : state === 'paused'
                  ? 'bg-amber-500 hover:bg-amber-600'
                  : 'bg-white hover:bg-gray-50 border border-gray-200'
            }`}
          >
            {state === 'recording' ? (
              <>
                <Mic className="w-6 h-6 text-[#1A1A1A]" />
                {/* Solid glow ring */}
                <div className="absolute inset-0 rounded-full border-2 border-red-500 shadow-[0_0_12px_rgba(239,68,68,0.8)]" />
              </>
            ) : state === 'paused' ? (
              <Pause className="w-6 h-6 text-[#1A1A1A]" />
            ) : (
              <Mic className="w-6 h-6 text-gray-600" />
            )}
            
            {/* Recording duration badge */}
            {(state === 'recording' || state === 'paused') && (
              <div className="absolute -top-1 -right-1 px-2 py-0.5 bg-gray-900 text-[#1A1A1A] text-xs font-mono rounded-full">
                {formatTime(duration)}
              </div>
            )}
          </motion.button>
        )}
      </div>
    </>
  );
};

export default ReadingRecorder;
